'use client'

import { Button } from '@/components/ui/button'
import { resolveDefaultAppUrl } from '@/features/preferences/utils/resolve-default-app-url'
import {
  useGetAuthMeQuery,
  useGetPreferencesQuery
} from '@/stores/api/application-api'

import { useRouter, usePathname } from 'next/navigation'

import { ArrowLeft, Home } from 'lucide-react'

export default function NotFound() {
  const router = useRouter()
  const pathname = usePathname()
  const { data: prefs } = useGetPreferencesQuery()
  const { data: authMe } = useGetAuthMeQuery()

  const orgSlug = pathname?.split('/').filter(Boolean)[0] ?? null

  const handleGoHome = () => {
    if (orgSlug) {
      router.push(
        resolveDefaultAppUrl(orgSlug, prefs, authMe) ?? '/select-organization'
      )
    } else {
      router.push('/')
    }
  }

  const handleGoBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back()
    } else {
      handleGoHome()
    }
  }

  return (
    <div className="bg-background flex min-h-screen flex-col items-center justify-center px-4">
      {/* Radar stage: three concentric primary rings ping outward from a
          drifting center pin. Staggered delays (0 / 1.13 / 2.26s) over the
          3.4s loop space the rings into evenly-spaced thirds. */}
      <div className="relative mb-7 h-[220px] w-[220px]">
        <div
          className="border-primary error-ping absolute inset-0 rounded-full border"
          style={{
            animation:
              'error-ping 3.4s cubic-bezier(0.2, 0.6, 0.3, 1) infinite',
            opacity: 0
          }}
        />
        <div
          className="border-primary error-ping absolute inset-0 rounded-full border"
          style={{
            animation:
              'error-ping 3.4s cubic-bezier(0.2, 0.6, 0.3, 1) 1.13s infinite',
            opacity: 0
          }}
        />
        <div
          className="border-primary error-ping absolute inset-0 rounded-full border"
          style={{
            animation:
              'error-ping 3.4s cubic-bezier(0.2, 0.6, 0.3, 1) 2.26s infinite',
            opacity: 0
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            <div
              className="bg-primary error-drift relative z-10 h-3.5 w-3.5 rounded-full"
              style={{
                boxShadow: '0 0 0 4px oklch(from var(--primary) l c h / 15%)',
                animation: 'error-drift 4s ease-in-out infinite'
              }}
            />
            <div
              className="pointer-events-none absolute -inset-4 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, oklch(from var(--primary) l c h / 15%), transparent 65%)'
              }}
            />
          </div>
        </div>
      </div>

      <div className="text-muted-foreground mb-3.5 font-mono text-[11px] font-medium uppercase tracking-[0.14em]">
        Error · 404 · Not Found
      </div>
      <h1 className="text-foreground mb-2.5 text-3xl font-semibold tracking-tight">
        We can&apos;t locate that page
      </h1>
      <p className="text-muted-foreground mb-8 max-w-md text-center text-[15px] leading-relaxed">
        The route you followed doesn&apos;t lead anywhere we know about. It may
        have been moved, archived, or never existed.
      </p>

      <div className="flex items-center gap-2.5">
        <Button onClick={handleGoHome}>
          <Home className="h-4 w-4" />
          Go to Home
        </Button>
        <Button variant="outline" onClick={handleGoBack}>
          <ArrowLeft className="h-4 w-4" />
          Go Back
        </Button>
      </div>
    </div>
  )
}
